import { ApplicationController, raw } from '@aha-app/mvc';
import type { ComponentProps, TableElement } from '../../../types/aha-editor';
import { type CellInfo, getTableStructureFromElement } from '../TableStructure';
import { Editor, Node, Path, Text } from 'slate';
import { ReactEditor } from 'slate-react';
import type TableSelection from '../TableSelection';

export interface TableControllerProps extends ComponentProps<TableElement> {
  editor: Editor;
  tableSelection: TableSelection;
  setTableSelection: (tableSelection: TableSelection) => void;
}

export interface TableControllerState {
  // The table element that this controller is attached to.
  tableElement: TableElement;
  isSelecting: boolean;
  tableSelection: TableSelection;
  hoveredResizeHandleIndex: number | null;
  draggingResizeHandleIndex: number | null;
}

class TableController extends ApplicationController<
  TableControllerState,
  TableControllerProps
> {
  editor: Editor;
  clickedAnchorCell: null | CellInfo;

  async initialize({ editor, element, tableSelection }: TableControllerProps) {
    this.editor = editor;
    this.clickedAnchorCell = null;
    this.state.tableSelection = tableSelection;
    this.state.isSelecting = false;
    this.state.tableElement = element;
    this.state.hoveredResizeHandleIndex = null;
    this.state.draggingResizeHandleIndex = null;
  }

  changeProps({ element }: TableControllerProps) {
    if (element !== raw(this.state.tableElement)) {
      this.state.tableElement = element;
    }
  }

  get tableStructure() {
    return getTableStructureFromElement(
      this.editor,
      raw(this.state.tableElement)
    );
  }

  actionUpdateSelection(event: MouseEvent) {
    const { selection } = this.editor;
    const nativeSelection = window.getSelection();

    const element = raw(this.state.tableElement);
    const tablePath = ReactEditor.findPath(this.editor, element);

    let range = nativeSelection
      ? ReactEditor.toSlateRange(this.editor, nativeSelection, {
          exactMatch: false,
          suppressThrow: true,
        })
      : selection;
    if (!range) return;

    // Table selection behaves strangely in Firefox. Use the anchor cell
    // from the original table click to make table selection more consistent.
    if (this.clickedAnchorCell) {
      range = {
        ...range,
        anchor: Editor.point(this.editor, [
          ...tablePath,
          ...this.clickedAnchorCell.relativePath,
        ]),
      };
    }

    this.state.tableSelection.isUpdatingSelection = true;

    let targetNode: Node | undefined;

    // There are elements within the editor that aren't actually Slate nodes
    // including toolbars, resize handles etc. If we mouseover one of those,
    // we don't want to blow up this function so this is wrapped in try/catch.
    try {
      targetNode = ReactEditor.toSlateNode(
        this.editor,
        event.target as Element
      );
    } catch (e) {}

    if (!targetNode) {
      this.state.tableSelection.extendSelectionToPath(
        this.editor,
        range,
        range.focus.path
      );
      return;
    }

    let targetText: Text | Node | null = null;
    if (Text.isText(targetNode)) {
      targetText = targetNode;
    } else {
      const [textNode] = Node.texts(targetNode);
      if (textNode) {
        targetText = textNode[0];
      }
    }

    if (targetText && targetText === targetNode) {
      this.state.tableSelection.extendSelectionToPath(
        this.editor,
        range,
        ReactEditor.findPath(this.editor, targetText)
      );
      return;
    }

    if (
      Path.isAncestor(
        ReactEditor.findPath(this.editor, targetNode),
        range.focus.path
      )
    ) {
      this.state.tableSelection.extendSelectionToPath(
        this.editor,
        range,
        range.focus.path
      );
      return;
    }

    if (!targetText) return;

    this.state.tableSelection.extendSelectionToPath(
      this.editor,
      range,
      ReactEditor.findPath(this.editor, targetText)
    );
  }

  actionSetIsSelecting(isSelecting: boolean) {
    this.state.isSelecting = isSelecting;
  }

  actionSetHoveredResizeHandle(index: number | null) {
    this.state.hoveredResizeHandleIndex = index;
  }

  actionSetDraggingResizeHandle(index: number | null) {
    this.state.draggingResizeHandleIndex = index;
  }
}

export default TableController;
